import { z } from 'zod'

/**
 * Settings contracts (Screen 6). Pinned to the backend's GET/PUT /api/settings
 * and POST /api/settings/test-kh shapes — the backend is built to these exact
 * wire formats in parallel.
 *
 * The server owns the settings schema: sections and fields are RENDERED
 * generically from these descriptors, so adding/removing a setting is a
 * server-only change. Every field value is a decimal/int STRING on the wire
 * (currency "125.00", integer "3", email "…") — kept as a string here and never
 * parsed to a float. KH API credentials are deliberately NOT in this contract;
 * they live in the server's ENV file.
 */

export const settingTypeSchema = z.enum(['currency', 'integer', 'email'])
export type SettingType = z.infer<typeof settingTypeSchema>

export const settingFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: settingTypeSchema,
  value: z.string(),
  help: z.string(),
})
export type SettingField = z.infer<typeof settingFieldSchema>

export const settingsSectionSchema = z.object({
  title: z.string(),
  settings: z.array(settingFieldSchema),
})
export type SettingsSection = z.infer<typeof settingsSectionSchema>

export const settingsResponseSchema = z.object({
  sections: z.array(settingsSectionSchema),
  generated_at: z.string(),
})
export type SettingsResponse = z.infer<typeof settingsResponseSchema>

/**
 * 400 body from PUT /api/settings when a change is invalid. `errors` maps the
 * offending setting key to a human message rendered under that field's input.
 */
export const settingsValidationSchema = z.object({
  detail: z.object({
    code: z.literal('VALIDATION'),
    errors: z.record(z.string(), z.string()),
  }),
})
export type SettingsValidation = z.infer<typeof settingsValidationSchema>

// ---- POST /api/settings/test-kh ----

export const testKhStatusSchema = z.enum([
  'connected',
  'auth_failed',
  'unreachable',
  'dry_run',
  'disabled',
])
export type TestKhStatus = z.infer<typeof testKhStatusSchema>

export const testKhResultSchema = z.object({
  status: testKhStatusSchema,
  message: z.string(),
})
export type TestKhResult = z.infer<typeof testKhResultSchema>
